import type { IWorkspaceService, ThreadUnreadCounts } from "@/services/interfaces/IWorkspaceService";
import { LEGACY_QUEST_KEY } from "@/services/interfaces/IWorkspaceService";
import type {
  WorkspaceListEntry,
  WorkspaceMember,
  WorkspaceMessage,
  WorkspaceMessageType,
} from "@/lib/types";
import type { ReactionEmoji } from "@/lib/workspace/reactions";
import type { ThreadKey } from "@/lib/workspace/threads";
import { parseAttachments, uniformAttachmentKind } from "@/lib/workspace/attachments";
import type { AttachedFile, RootStorageEntry } from "@/lib/workspace/files";
import type { ContentI18n } from "@/lib/questContent/types";
import { supabase } from "@/lib/supabase";
import { getChainConfig } from "@/config/contracts";
import { isVolunteerQuest } from "@/lib/utils";
import { useAuthStore } from "@/stores/useAuthStore";

const QUEST_FILES_BUCKET = "quest-files";

function getCurrentUserId(): string | null {
  return useAuthStore.getState().session?.userId ?? null;
}

// ─── Row types ───────────────────────────────────────────

interface WorkspaceMemberRow {
  id: string;
  quest_id: string;
  user_id: string;
  role: string;
  joined_at: string;
}

interface WorkspaceReactionRow {
  user_id: string;
  emoji: string;
}

interface WorkspaceMessageRow {
  id: string;
  quest_id: string;
  sender_id: string | null;
  sender_role: string;
  content: string;
  message_type: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  task_id: string | null;
  workspace_message_reactions?: WorkspaceReactionRow[] | null;
}

type ProfileMap = Map<string, { displayName: string; avatarUrl: string | null }>;

interface WorkspaceListRow {
  quest_id: string;
  role: string;
  joined_at: string;
  quests: {
    id: string;
    title: string;
    status: string;
    is_template_anchor: boolean | null;
    total_budget_usd: number | string | null;
    i18n: ContentI18n | null;
    source_lang: string | null;
  };
}

// ─── Mappers ─────────────────────────────────────────────

function mapToMember(row: WorkspaceMemberRow, profiles: ProfileMap): WorkspaceMember {
  const profile = profiles.get(row.user_id);
  return {
    id: row.id,
    questId: row.quest_id,
    userId: row.user_id,
    role: row.role as WorkspaceMember["role"],
    joinedAt: row.joined_at,
    displayName: profile?.displayName,
    avatarUrl: profile?.avatarUrl ?? null,
  };
}

function mapToMessage(row: WorkspaceMessageRow, profiles: ProfileMap): WorkspaceMessage {
  const profile = row.sender_id ? profiles.get(row.sender_id) : undefined;
  return {
    id: row.id,
    questId: row.quest_id,
    senderId: row.sender_id,
    senderRole: row.sender_role as WorkspaceMessage["senderRole"],
    content: row.content,
    messageType: row.message_type as WorkspaceMessage["messageType"],
    metadata: row.metadata,
    createdAt: row.created_at,
    taskId: row.task_id,
    senderDisplayName: profile?.displayName,
    senderAvatarUrl: profile?.avatarUrl ?? null,
    reactions: (row.workspace_message_reactions ?? []).map((r) => ({
      userId: r.user_id,
      emoji: r.emoji,
    })),
  };
}

// ─── Service ─────────────────────────────────────────────

/**
 * Calls a chat RPC scoped to the active network (p_chain_id, 20261015120000).
 * Before that migration is applied the RPC has no such parameter and PostgREST
 * answers PGRST202, so retry once without it: the unscoped result is what the
 * frontend got before, and callers still filter it by chain on the client
 * (useUnreadCounts keeps only the chain-scoped workspace list's quests).
 * A PGRST202 from the retry means the RPC itself is missing; it is returned
 * as-is for the caller's own fallback.
 *
 * The miss is remembered for the page load, so the 5-10 s polls make one
 * request, not two, while the migration is pending. A migration applied
 * mid-session is picked up on the next load; until then the client filter
 * keeps the result correct.
 */
type ChainScopedRpc = "get_thread_unread_counts" | "get_workspace_inbox";
const chainScopeUnsupported = new Set<ChainScopedRpc>();

async function rpcChainScoped(fn: ChainScopedRpc) {
  if (chainScopeUnsupported.has(fn)) return supabase.rpc(fn);
  const first = await supabase.rpc(fn, { p_chain_id: getChainConfig().chainId });
  if (first.error?.code !== "PGRST202") return first;
  chainScopeUnsupported.add(fn);
  return supabase.rpc(fn);
}

/** Test seam: forget remembered PGRST202 misses. */
export function resetChainScopedRpcCache(): void {
  chainScopeUnsupported.clear();
}

/** Newest-message preview and counterpart for one quest's chat. */
type InboxRow = {
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageType: string | null;
  lastMessageI18nKey?: string | null;
  lastMessageAttachmentKind?: string | null;
  counterpartName?: string | null;
  counterpartCount?: number;
};

export class SupabaseWorkspaceService implements IWorkspaceService {
  async getMyWorkspaces(userId: string): Promise<WorkspaceListEntry[]> {
    const chainId = getChainConfig().chainId;
    const { data, error } = await supabase
      .from("workspace_members")
      .select(
        `
        quest_id,
        role,
        joined_at,
        quests!inner (
          id,
          title,
          status,
          chain_id,
          is_template_anchor,
          total_budget_usd,
          i18n,
          source_lang
        )
      `,
      )
      .eq("user_id", userId)
      .eq("quests.chain_id", chainId)
      .order("joined_at", { ascending: false });

    if (error) throw error;
    if (!data) return [];

    const questIds = (data as unknown as WorkspaceListRow[]).map((r) => r.quest_id);

    // Fetch task statuses per quest to derive accurate workspace status
    const { data: allTasks } = await supabase
      .from("tasks")
      .select("quest_id, status")
      .in("quest_id", questIds);

    const taskStatusByQuest = new Map<string, string[]>();
    for (const t of allTasks ?? []) {
      const existing = taskStatusByQuest.get(t.quest_id) ?? [];
      existing.push(t.status);
      taskStatusByQuest.set(t.quest_id, existing);
    }

    // Derive quest status from task statuses (matches QuestStatusCard logic)
    function deriveStatus(questStatus: string, taskStatuses: string[]): string {
      if (questStatus === "completed" || questStatus === "closed") return questStatus;
      if (taskStatuses.length > 0 && taskStatuses.every((s) => s === "completed" || s === "approved")) return "completed";
      if (taskStatuses.some((s) => s === "submitted")) return "submitted";
      if (taskStatuses.some((s) => s === "changes_requested")) return "changes_requested";
      if (taskStatuses.some((s) => s === "in_progress" || s === "assigned")) return "in_progress";
      return questStatus;
    }

    const inbox = await this.fetchInboxRows(questIds);

    return (data as unknown as WorkspaceListRow[]).map((row) => {
      const box = inbox.get(row.quest_id);
      const taskStatuses = taskStatusByQuest.get(row.quest_id) ?? [];
      const derivedStatus = deriveStatus(row.quests.status, taskStatuses);
      return {
        questId: row.quest_id,
        questTitle: row.quests.title,
        questStatus: derivedStatus as WorkspaceListEntry["questStatus"],
        role: row.role as WorkspaceListEntry["role"],
        joinedAt: row.joined_at,
        lastMessageAt: box?.lastMessageAt ?? null,
        lastMessagePreview: box?.lastMessagePreview ?? null,
        lastMessageType: box?.lastMessageType ?? null,
        lastMessageI18nKey: box?.lastMessageI18nKey ?? null,
        lastMessageAttachmentKind: box?.lastMessageAttachmentKind ?? null,
        counterpartName: box?.counterpartName,
        counterpartCount: box?.counterpartCount,
        isTemplateAnchor: row.quests.is_template_anchor ?? false,
        // A null budget is unknown, not free — only an explicit 0 is volunteer.
        isVolunteer: row.quests.total_budget_usd != null && isVolunteerQuest(Number(row.quests.total_budget_usd)),
        questI18n: row.quests.i18n ?? null,
        questSourceLang: row.quests.source_lang ?? null,
      };
    });
  }

  /**
   * Newest visible message + counterpart per quest from get_workspace_inbox
   * (20260925100000), scoped to the active network when 20261015120000 is
   * applied (see rpcChainScoped). Before 20260925100000 is applied (PGRST202) falls back
   * to scanning recent messages directly — capped, and with no counterpart —
   * so the inbox still renders during rollout. Any other RPC failure takes the
   * same path: previews and names are decoration, and a throw here would empty
   * the membership list every chat badge and button hangs off.
   */
  private async fetchInboxRows(questIds: string[]): Promise<Map<string, InboxRow>> {
    const out = new Map<string, InboxRow>();
    if (questIds.length === 0) return out;

    const { data, error } = await rpcChainScoped("get_workspace_inbox");
    if (!error) {
      for (const r of (data ?? []) as {
        quest_id: string; last_message_at: string | null; last_message_preview: string | null;
        last_message_type: string | null; last_message_i18n_key?: string | null;
        // Absent until 20261001140000 is applied: the row then shows the generic 📎.
        last_message_attachment_kind?: string | null;
        counterpart_name: string | null; counterpart_count: number;
      }[]) {
        out.set(r.quest_id, {
          lastMessageAt: r.last_message_at,
          lastMessagePreview: r.last_message_preview,
          lastMessageType: r.last_message_type,
          lastMessageI18nKey: r.last_message_i18n_key ?? null,
          lastMessageAttachmentKind: r.last_message_attachment_kind ?? null,
          counterpartName: r.counterpart_name,
          counterpartCount: r.counterpart_count,
        });
      }
      return out;
    }
    if (error.code !== "PGRST202") {
      console.error("[WorkspaceService] get_workspace_inbox failed, using direct query:", error);
    }

    const { data: recent } = await supabase
      .from("workspace_messages")
      .select("quest_id, content, created_at, message_type, metadata")
      .in("quest_id", questIds)
      .order("created_at", { ascending: false })
      .limit(500);
    for (const msg of recent ?? []) {
      if (out.has(msg.quest_id)) continue;
      // Only system rows: a member writes their own message's metadata.
      const key = msg.message_type === "system"
        ? (msg.metadata as { i18n_key?: unknown } | null)?.i18n_key
        : undefined;
      out.set(msg.quest_id, {
        lastMessageAt: msg.created_at,
        lastMessagePreview: msg.content.slice(0, 120),
        lastMessageType: msg.message_type,
        lastMessageI18nKey: typeof key === "string" ? key : null,
        lastMessageAttachmentKind: msg.message_type === "attachment"
          ? uniformAttachmentKind(msg.metadata as Record<string, unknown> | null)
          : null,
      });
    }
    return out;
  }

  async createWorkspace(questId: string, creatorId: string): Promise<void> {
    const { error } = await supabase.from("workspace_members").insert({
      quest_id: questId,
      user_id: creatorId,
      role: "creator",
    });

    // Surfaced, not swallowed: without this row the creator is not a member,
    // so `is_workspace_member` denies them their own workspace. The caller
    // must not navigate into it.
    if (error) throw error;

    // Deliberately not awaited. The seed message is a courtesy line, and the
    // workspace is already usable without it; making the caller's navigation
    // wait on a second round-trip would delay the surface it is about to show.
    // A failure here leaves an empty-but-working workspace, which is why it is
    // logged rather than thrown.
    //
    // The copy stays in English here rather than at the callsite: this row is
    // persisted once and read by every member, so there is no single viewer
    // whose locale it could be rendered in (see the content-i18n rule —
    // stored content keeps the words it was written with).
    void supabase
      .from("workspace_messages")
      .insert({
        quest_id: questId,
        sender_id: null,
        sender_role: "system",
        content: "🚀 Workspace created — waiting for a worker to accept this quest.",
        message_type: "system",
      })
      .then(({ error: msgError }) => {
        if (msgError) console.error("[WorkspaceService] seed message error:", msgError);
      });
  }

  async addWorkerMember(questId: string, userId: string): Promise<void> {
    const { error } = await supabase.from("workspace_members").insert({
      quest_id: questId,
      user_id: userId,
      role: "worker",
    });
    if (error) {
      console.error("[SupabaseWorkspaceService] addWorkerMember failed:", error);
    }
  }

  async getWorkspaceMembers(questId: string): Promise<WorkspaceMember[]> {
    const { data, error } = await supabase
      .from("workspace_members")
      .select(
        `
        id,
        quest_id,
        user_id,
        role,
        joined_at
      `,
      )
      .eq("quest_id", questId);

    if (error) throw error;
    const rows = (data as unknown as WorkspaceMemberRow[]) ?? [];

    // Resolved separately, through public_profiles: `profiles!inner` here
    // used to make a denied embed drop the ROW, not just null the name, so a
    // non-admin viewer could see an empty or truncated member list — which is
    // exactly the identity the chat header and "with {name}" label read from.
    const userIds = rows.map((r) => r.user_id).filter((id): id is string => !!id);
    const profiles = await this.resolveProfiles(userIds);

    return rows.map((row) => mapToMember(row, profiles));
  }

  async sendMessage(
    questId: string,
    content: string,
    messageType: WorkspaceMessageType = "text",
    metadata?: Record<string, unknown>,
    senderRoleOverride?: string,
    taskId?: string | null,
    toGroup = false,
  ): Promise<WorkspaceMessage> {
    const userId = getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");
    if (toGroup && taskId) {
      throw new Error("A group message cannot also target a task thread");
    }

    // Determine sender_role: use override if provided, else resolve from membership
    let senderRole: string;
    if (senderRoleOverride) {
      senderRole = senderRoleOverride;
    } else if (messageType === "system") {
      senderRole = "system";
    } else {
      const { data: member } = await supabase
        .from("workspace_members")
        .select("role")
        .eq("quest_id", questId)
        .eq("user_id", userId)
        .single();
      senderRole = member?.role ?? "creator";
    }

    const { data, error } = await supabase
      .from("workspace_messages")
      .insert({
        quest_id: questId,
        sender_id: messageType === "system" ? null : userId,
        sender_role: senderRole,
        content,
        message_type: messageType,
        metadata: metadata ?? null,
        // Omitted/NULL is redirected by the DB trigger to the writer's sole
        // entitled task in this quest, UNLESS to_group marks an explicit
        // group-chat post; do not compute entitlement here. to_group is sent
        // only when true so ordinary sends never depend on the column.
        task_id: toGroup ? null : (taskId ?? null),
        ...(toGroup ? { to_group: true } : {}),
      })
      .select(
        `
        id,
        quest_id,
        sender_id,
        sender_role,
        content,
        message_type,
        metadata,
        created_at,
        task_id
      `,
      )
      .single();

    if (error) throw error;
    const row = data as unknown as WorkspaceMessageRow;
    const profiles = await this.resolveProfiles(row.sender_id ? [row.sender_id] : []);
    return mapToMessage(row, profiles);
  }

  /**
   * Resolves user identities (chat senders AND workspace members) through
   * `public_profiles`, the definer view created by 20260902140000
   * specifically so non-admin code can display OTHER users' profiles.
   * `profiles` itself is owner-or-admin since 20260413000003, and a
   * PostgREST embed against a denied row comes back as HTTP 200 with `null`
   * — not an error — so every non-admin viewer silently saw the other party
   * in a quest chat as "Unknown". Worse, an `!inner` embed (as
   * `getWorkspaceMembers` used) drops the ROW outright on denial rather than
   * nulling a field, so a non-admin could see an empty or truncated member
   * list.
   *
   * A second round trip, deliberately, not an embed: relationship inference
   * through a definer view is not reliable, and the accepted cost is one
   * extra query per page/list.
   *
   * A failure here must never fail the message/member load — identity
   * resolution can never be what removes a person from the conversation.
   */
  private async resolveProfiles(userIds: string[]): Promise<ProfileMap> {
    const profiles: ProfileMap = new Map();
    const uniqueIds = [...new Set(userIds)];
    if (uniqueIds.length === 0) return profiles;

    const { data: profileRows, error } = await supabase
      .from("public_profiles")
      .select("id, display_name, avatar_url")
      .in("id", uniqueIds);

    if (error) {
      console.error("[WorkspaceService] resolveProfiles error:", error);
      return profiles;
    }

    for (const p of (profileRows ?? []) as Array<{
      id: string;
      display_name: string;
      avatar_url: string | null;
    }>) {
      profiles.set(p.id, { displayName: p.display_name, avatarUrl: p.avatar_url });
    }
    return profiles;
  }

  async getMessages(
    questId: string,
    before?: string,
    limit: number = 50,
    taskId?: string | null,
  ): Promise<{ messages: WorkspaceMessage[]; nextCursor: string | null }> {
    let query = supabase
      .from("workspace_messages")
      .select(
        `
        id,
        quest_id,
        sender_id,
        sender_role,
        content,
        message_type,
        metadata,
        created_at,
        task_id,
        workspace_message_reactions (
          user_id,
          emoji
        )
      `,
      )
      .eq("quest_id", questId);

    // `.is(col, undefined)` does NOT mean "skip this filter" in postgrest-js —
    // branch explicitly. `undefined`/`null` both mean the General thread.
    query = taskId ? query.eq("task_id", taskId) : query.is("task_id", null);

    query = query
      .order("created_at", { ascending: false })
      .limit(limit + 1); // Fetch one extra to determine if there are more

    if (before) {
      query = query.lt("created_at", before);
    }

    const { data, error } = await query;
    if (error) throw error;
    if (!data) return { messages: [], nextCursor: null };

    const rows = data as unknown as WorkspaceMessageRow[];
    const hasMore = rows.length > limit;
    const messageRows = hasMore ? rows.slice(0, limit) : rows;

    const senderIds = messageRows
      .map((r) => r.sender_id)
      .filter((id): id is string => !!id);
    const profiles = await this.resolveProfiles(senderIds);

    // Reverse to chronological order (oldest first)
    const messages = messageRows.map((row) => mapToMessage(row, profiles)).reverse();
    const lastRow = messageRows[messageRows.length - 1];
    const nextCursor = hasMore && lastRow ? lastRow.created_at : null;

    return { messages, nextCursor };
  }

  async getMessageAttachments(questId: string): Promise<AttachedFile[]> {
    // Paged to exhaustion: a single capped read silently dropped the oldest
    // files on long quests, and any member could push real files out of the
    // window by posting attachment-bearing messages. `id` breaks created_at
    // ties so offsets stay stable across pages. PAGE must stay under
    // PostgREST's max_rows (1000), or a full page reads as the last one.
    const PAGE = 500;
    const out: AttachedFile[] = [];
    for (let offset = 0; ; offset += PAGE) {
      const { data, error } = await supabase
        .from("workspace_messages")
        .select("metadata, created_at")
        .eq("quest_id", questId)
        .not("metadata->attachments", "is", null)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(offset, offset + PAGE - 1);
      if (error) throw error;

      const rows = (data ?? []) as Array<{ metadata: Record<string, unknown> | null; created_at: string }>;
      for (const row of rows) {
        for (const attachment of parseAttachments(row.metadata)) {
          out.push({ attachment, createdAt: row.created_at });
        }
      }
      if (rows.length < PAGE) return out;
    }
  }

  async listRootFiles(questId: string): Promise<RootStorageEntry[]> {
    const { data, error } = await supabase.storage
      .from(QUEST_FILES_BUCKET)
      .list(questId, { sortBy: { column: "created_at", order: "desc" } });
    if (error) throw error;

    return (data ?? [])
      // Non-recursive `list` returns pseudo-entries for subfolders
      // ({questId}/chat/ and {questId}/submissions/) as rows with id: null
      // and metadata: null. Filter those out too, or they render as 0-byte
      // "files" with a working Delete and a broken Download in the panel.
      .filter((f) => f.name !== ".emptyFolderPlaceholder" && f.id !== null)
      .map((f) => ({
        name: f.name,
        size: f.metadata?.size ?? 0,
        createdAt: f.created_at ?? "",
      }));
  }

  async removeFiles(paths: string[]): Promise<void> {
    const { error } = await supabase.storage.from(QUEST_FILES_BUCKET).remove(paths);
    if (error) throw error;
  }

  async uploadFile(path: string, file: File): Promise<void> {
    const { error } = await supabase.storage.from(QUEST_FILES_BUCKET).upload(path, file);
    if (error) throw error;
  }

  async downloadFile(path: string): Promise<Blob> {
    const { data, error } = await supabase.storage.from(QUEST_FILES_BUCKET).download(path);
    if (error) throw error;
    return data;
  }

  async getApplicants(questId: string): Promise<Array<{ userId: string; displayName: string; avatarUrl: string | null; taskId: string; appliedAt: string }>> {
    // Get all tasks for this quest
    const { data: tasks } = await supabase
      .from("tasks")
      .select("id")
      .eq("quest_id", questId);

    if (!tasks || tasks.length === 0) return [];

    const taskIds = tasks.map((t) => t.id);

    // Get right-swipes on these tasks with profile info
    const { data: swipes } = await supabase
      .from("swipe_history")
      .select("user_id, task_id, created_at, profiles!inner(display_name, avatar_url)")
      .in("task_id", taskIds)
      .eq("direction", "right")
      .order("created_at", { ascending: false });

    if (!swipes) return [];

    return (swipes as unknown as Array<{
      user_id: string;
      task_id: string;
      created_at: string;
      profiles: { display_name: string; avatar_url: string | null };
    }>).map((s) => ({
      userId: s.user_id,
      displayName: s.profiles.display_name,
      avatarUrl: s.profiles.avatar_url,
      taskId: s.task_id,
      appliedAt: s.created_at,
    }));
  }

  async updateReadCursor(questId: string): Promise<void> {
    const userId = getCurrentUserId();
    if (!userId) return;

    const { error } = await supabase
      .from("workspace_read_cursors")
      .upsert(
        {
          quest_id: questId,
          user_id: userId,
          last_read_at: new Date().toISOString(),
        },
        { onConflict: "quest_id,user_id" },
      );

    if (error) console.error("[WorkspaceService] updateReadCursor error:", error);
  }

  async getUnreadCounts(questIds: string[]): Promise<Map<string, number>> {
    const result = new Map<string, number>();
    if (questIds.length === 0) return result;

    const userId = getCurrentUserId();
    if (!userId) return result;

    // Get read cursors for all workspaces
    const { data: cursors } = await supabase
      .from("workspace_read_cursors")
      .select("quest_id, last_read_at")
      .eq("user_id", userId)
      .in("quest_id", questIds);

    const cursorMap = new Map<string, string>();
    for (const c of cursors ?? []) {
      cursorMap.set(c.quest_id, c.last_read_at);
    }

    // For each workspace, count messages after the cursor
    for (const questId of questIds) {
      const lastRead = cursorMap.get(questId);

      let query = supabase
        .from("workspace_messages")
        .select("id", { count: "exact", head: true })
        .eq("quest_id", questId);

      if (lastRead) {
        query = query.gt("created_at", lastRead);
      }

      const { count } = await query;
      if (count && count > 0) {
        result.set(questId, count);
      }
    }

    return result;
  }

  async getThreadUnreadCounts(questIds: string[]): Promise<ThreadUnreadCounts> {
    const out: ThreadUnreadCounts = new Map();
    const { data, error } = await rpcChainScoped("get_thread_unread_counts");
    if (error) {
      // Frontend deployed ahead of migration 20260924100000: keep the old
      // per-quest badge rather than showing nothing.
      if (error.code === "PGRST202") {
        const legacy = await this.getUnreadCounts(questIds);
        for (const [questId, n] of legacy) out.set(questId, new Map([[LEGACY_QUEST_KEY, n]]));
        return out;
      }
      throw error;
    }
    for (const row of (data ?? []) as { quest_id: string; thread_key: string; unread: number }[]) {
      if (!out.has(row.quest_id)) out.set(row.quest_id, new Map());
      out.get(row.quest_id)!.set(row.thread_key, row.unread);
    }
    return out;
  }

  async markThreadRead(questId: string, thread: ThreadKey, readUpTo?: string): Promise<void> {
    const { error } = await supabase.rpc("mark_thread_read", {
      p_quest_id: questId,
      p_thread_key: thread,
      p_read_up_to: readUpTo ?? null,
    });
    if (!error) return;
    // Frontend deployed ahead of migration 20260924100000: counts come from
    // the legacy per-quest cursor then (see getThreadUnreadCounts), so
    // advance that one instead. Expected during rollout — not an error.
    if (error.code === "PGRST202") {
      await this.updateReadCursor(questId);
      return;
    }
    console.error("[WorkspaceService] markThreadRead error:", error);
  }

  async setReaction(messageId: string, emoji: ReactionEmoji): Promise<void> {
    const userId = getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    const { error } = await supabase
      .from("workspace_message_reactions")
      .upsert(
        { message_id: messageId, user_id: userId, emoji },
        { onConflict: "message_id,user_id" },
      );

    if (error) throw error;
  }

  async clearReaction(messageId: string): Promise<void> {
    const userId = getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    const { error } = await supabase
      .from("workspace_message_reactions")
      .delete()
      .eq("message_id", messageId)
      .eq("user_id", userId);

    if (error) throw error;
  }
}
